import React, { useRef, useEffect, useCallback, useState } from 'react';
import DragHandleMenu from './DragHandleMenu';
import { useTaskNodeHandlers } from './hooks';
import { NOTE_NODE_WIDTH, NOTE_NODE_HEIGHT } from '../utils/noteUtils';

const noopConnectionMenu = { openMenu: () => {} };

/**
 * NoteNode — host-anchored comment card on the flow canvas.
 * Occupies layout space like other nodes; not a work step.
 */
const NoteNode = ({
  task,
  position,
  isSelected,
  isDragging,
  isAnimating,
  isHoveredFromHUD = false,
  onNodeHoverForHUD,
  onNodeLeaveForHUD,
  onMouseDown,
  onClick,
  onRename,
  onDimensionsChange,
  onDelete,
  onCopy,
  onDuplicate,
  layoutSurfaceEpoch: _layoutSurfaceEpoch = 0,
}) => {
  const nodeRef = useRef(null);
  const textRef = useRef(null);
  const dragHandleMenuRef = useRef(null);
  const [draft, setDraft] = useState(task.name || '');
  const autoFocusedRef = useRef(false);

  const { handleNodeMouseDown } = useTaskNodeHandlers({
    onMouseDown,
    connectionMenu: noopConnectionMenu,
  });

  useEffect(() => {
    setDraft(task.name || '');
  }, [task.id, task.name]);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const maxHeight = parseFloat(getComputedStyle(el).maxHeight) || 196;
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
  }, [draft]);

  useEffect(() => {
    const el = nodeRef.current;
    if (!el || !onDimensionsChange) return undefined;

    const report = () => {
      onDimensionsChange(task.id, {
        width: NOTE_NODE_WIDTH,
        height: Math.max(NOTE_NODE_HEIGHT, el.offsetHeight),
      });
    };

    report();
    const observer = new ResizeObserver(report);
    observer.observe(el);
    return () => observer.disconnect();
  }, [task.id, onDimensionsChange, draft]);

  useEffect(() => {
    if (autoFocusedRef.current) return;
    if (task.name) return;
    autoFocusedRef.current = true;
    const rafId = requestAnimationFrame(() => {
      textRef.current?.focus();
    });
    return () => cancelAnimationFrame(rafId);
  }, [task.name]);

  useEffect(() => {
    const handleOpenDragMenu = (e) => {
      if (e.detail.taskId === task.id && dragHandleMenuRef.current) {
        const { handleRect } = e.detail;
        const viewportHeight = window.innerHeight;
        const menuHeight = 180;

        let top = handleRect.top;
        if (top + menuHeight > viewportHeight) {
          top = viewportHeight - menuHeight - 10;
        }

        let left = handleRect.left - 180;
        if (left < 10) {
          left = handleRect.right + 8;
        }

        dragHandleMenuRef.current.openAtPosition(left, top);
      }
    };
    window.addEventListener('openTaskDragMenu', handleOpenDragMenu);
    return () => window.removeEventListener('openTaskDragMenu', handleOpenDragMenu);
  }, [task.id]);

  const handleContextMenu = useCallback((e) => {
    const isInteractive = e.target.closest(
      'button, input, textarea, select, .task-node__drag-zone, .task-node__drag-handle',
    );
    if (!isInteractive && dragHandleMenuRef.current) {
      e.preventDefault();
      e.stopPropagation();
      dragHandleMenuRef.current.openAtPosition(e.clientX, e.clientY);
    }
  }, []);

  const commitDraft = useCallback(() => {
    const next = draft;
    if ((task.name || '') !== next) {
      onRename?.(task.id, next);
    }
  }, [draft, onRename, task.id, task.name]);

  const nodeClasses = [
    'note-node',
    isSelected && 'note-node--selected',
    isDragging && 'note-node--dragging',
    isAnimating && 'note-node--animating',
    isHoveredFromHUD && 'note-node--hud-hover',
  ].filter(Boolean).join(' ');

  return (
    <div
      ref={nodeRef}
      className={nodeClasses}
      data-task-id={task.id}
      style={{
        '--node-tx': `${position.x}px`,
        '--node-ty': `${position.y}px`,
        width: `${NOTE_NODE_WIDTH}px`,
      }}
      onMouseDown={handleNodeMouseDown}
      onClick={onClick}
      onContextMenu={handleContextMenu}
      onMouseEnter={() => onNodeHoverForHUD?.()}
      onMouseLeave={() => onNodeLeaveForHUD?.()}
    >
      <DragHandleMenu
        ref={dragHandleMenuRef}
        task={task}
        onDelete={onDelete}
        onCopy={onCopy}
        onDuplicate={onDuplicate}
      />

      <div className="note-node__chrome">
        <svg className="note-node__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span className="note-node__label">Note</span>
      </div>

      <textarea
        ref={textRef}
        className="note-node__text"
        value={draft}
        placeholder="Add a note…"
        rows={3}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commitDraft}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            setDraft(task.name || '');
            e.target.blur();
          }
        }}
      />
    </div>
  );
};

export default NoteNode;
